import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getCookie } from 'views/utils';

const resource_api_url = process.env.REACT_APP_RESOURCE_API_URL;

const ACTIVE = ['queued', 'running', 'rate_limited'];
const POLL_MS = 3000;
const POLL_RATE_LIMITED_MS = 20000;
const POLL_RETRY_MS = 10000;

const headers = () => ({ Authorization: getCookie('stratava_token') });

// localStorage can be unavailable (private window, blocked site data): the job still runs, it just
// cannot be picked up again after a reload.
const readTicket = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const writeTicket = (key, ticket) => {
  try {
    if (ticket) window.localStorage.setItem(key, ticket);
    else window.localStorage.removeItem(key);
  } catch (e) {
    /* ignore */
  }
};

// A job that runs in the background on the server (sync, analysis). Starting it returns a ticket, which is kept in
// the browser (localStorage[ticketKey]); the hook polls the job until it is done or failed. When the page is opened
// again the stored ticket is checked, so a running job shows up again. `onFinished(job)` runs when it ends.
export default function useBackgroundJob({ ticketKey, startPath, startBody, onFinished }) {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const timer = useRef(null);
  const finished = useRef(onFinished);
  finished.current = onFinished;

  const track = useCallback(
    (ticket) => {
      clearTimeout(timer.current);
      const poll = () => {
        axios
          .get(`${resource_api_url}/api/Sync_Job`, { params: { ticket }, headers: headers() })
          .then((res) => {
            if (!mounted.current) return;
            const j = res.data;
            setJob(j);
            if (j.status === 'done' || j.status === 'failed') {
              writeTicket(ticketKey, null);
              if (finished.current) finished.current(j);
              return;
            }
            timer.current = setTimeout(poll, j.status === 'rate_limited' ? POLL_RATE_LIMITED_MS : POLL_MS);
          })
          .catch((err) => {
            if (!mounted.current) return;
            const code = err.response?.status;
            if (code === 404) {
              writeTicket(ticketKey, null); // the ticket is not known any more
              setJob(null);
            } else if (code === 401 || code === 440) {
              setError('Session expired. Please sign in again; the job keeps running in the background.');
            } else {
              timer.current = setTimeout(poll, POLL_RETRY_MS);
            }
          });
      };
      poll();
    },
    [ticketKey]
  );

  useEffect(() => {
    mounted.current = true;
    const ticket = readTicket(ticketKey);
    if (ticket) track(ticket);
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
    };
  }, [ticketKey, track]);

  const start = async () => {
    setStarting(true);
    setError(null);
    setJob(null);
    try {
      const { data } = await axios.post(`${resource_api_url}${startPath}`, startBody || {}, { headers: headers() });
      writeTicket(ticketKey, data.ticket);
      setJob(data.job);
      track(data.ticket);
    } catch (err) {
      const code = err.response?.status;
      if (code === 404) setError('Strava is not connected yet. Connect Strava first.');
      else if (code === 401 || code === 440) setError('Session expired. Please sign in again.');
      else setError('Could not start. Please try again.');
    } finally {
      if (mounted.current) setStarting(false);
    }
  };

  const active = !!job && ACTIVE.includes(job.status);
  return { job, active, starting, error, start };
}
